import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '../RootState';
import {
    assignFromQueue,
    assignFromQueueBatch,
    assignVipByEmail,
    createTeam,
    getActivityPage,
    getAssignmentQueue,
    getDealDuplicates,
    getNotificationsPage,
    getSnapshot,
    mergeDeals,
    saveSellerConfig,
    saveVipOverride,
    updateTeam,
} from './api';
import {
    ActivityPage,
    BatchAssignResult,
    DealMergePayload,
    DuplicatesPage,
    NotificationsPage,
    PanelSnapshot,
    QueueAssignBatchPayload,
    QueueAssignPayload,
    QueuePage,
    SellerConfigPatch,
    TeamCreatePayload,
    TeamUpdatePatch,
    VipAssignByEmailPayload,
    VipOverridePatch,
} from './panel_comerciales.types';

interface PageParams {
    limit: number;
    offset: number;
}
interface DuplicatesParams extends PageParams {
    email?: string;
    owner?: string;
}

export const loadSnapshot = createAsyncThunk<
    PanelSnapshot,
    void | { period?: string },
    { state: RootState }
>('panel_comerciales/loadSnapshot', async (payload, thunkAPI) => {
    const token = thunkAPI.getState().User?.token ?? '';
    const period = (payload as { period?: string } | undefined)?.period;
    return getSnapshot(token, period);
});

export const saveSellerConfigThunk = createAsyncThunk<
    void,
    { sellerId: number; patch: SellerConfigPatch },
    { state: RootState }
>('panel_comerciales/saveSellerConfig', async ({ sellerId, patch }, thunkAPI) => {
    const token = thunkAPI.getState().User?.token ?? '';
    await saveSellerConfig(token, sellerId, patch);
    thunkAPI.dispatch(loadSnapshot());
});

export const saveVipOverrideThunk = createAsyncThunk<
    void,
    { clientId: string; patch: VipOverridePatch },
    { state: RootState }
>('panel_comerciales/saveVipOverride', async ({ clientId, patch }, thunkAPI) => {
    const token = thunkAPI.getState().User?.token ?? '';
    await saveVipOverride(token, clientId, patch);
    thunkAPI.dispatch(loadSnapshot());
});

export const assignVipByEmailThunk = createAsyncThunk<
    void,
    { payload: VipAssignByEmailPayload },
    { state: RootState }
>('panel_comerciales/assignVipByEmail', async ({ payload }, thunkAPI) => {
    const token = thunkAPI.getState().User?.token ?? '';
    await assignVipByEmail(token, payload);
    thunkAPI.dispatch(loadSnapshot());
});

export const createTeamThunk = createAsyncThunk<
    void,
    { payload: TeamCreatePayload },
    { state: RootState }
>('panel_comerciales/createTeam', async ({ payload }, thunkAPI) => {
    const token = thunkAPI.getState().User?.token ?? '';
    await createTeam(token, payload);
    thunkAPI.dispatch(loadSnapshot());
});

export const updateTeamThunk = createAsyncThunk<
    void,
    { teamId: number; patch: TeamUpdatePatch },
    { state: RootState }
>('panel_comerciales/updateTeam', async ({ teamId, patch }, thunkAPI) => {
    const token = thunkAPI.getState().User?.token ?? '';
    await updateTeam(token, teamId, patch);
    thunkAPI.dispatch(loadSnapshot());
});

// ---------------------------------------------------------------------------
// Sección "Estado de deals" (duplicados + cola)
// ---------------------------------------------------------------------------

/** Relee duplicados con la página/búsqueda vigentes del estado (ago-2026). */
const currentDuplicatesParams = (state: RootState): DuplicatesParams => {
    const ds = state.panel_comerciales.dealsStatus;
    return {
        limit: ds.duplicates.limit,
        offset: ds.duplicates.offset,
        email: ds.duplicatesSearch.email || undefined,
        owner: ds.duplicatesSearch.owner || undefined,
    };
};

export const loadDealDuplicates = createAsyncThunk<
    DuplicatesPage,
    DuplicatesParams,
    { state: RootState }
>('panel_comerciales/loadDealDuplicates', async (params, thunkAPI) => {
    const token = thunkAPI.getState().User?.token ?? '';
    return getDealDuplicates(token, {
        limit: params.limit,
        offset: params.offset,
        email: params.email || undefined,
        owner: params.owner || undefined,
    });
});

export const mergeDealsThunk = createAsyncThunk<
    { survivingDealId: number; ownerChanged: boolean },
    { payload: DealMergePayload },
    { state: RootState }
>('panel_comerciales/mergeDeals', async ({ payload }, thunkAPI) => {
    const token = thunkAPI.getState().User?.token ?? '';
    const result = await mergeDeals(token, payload);
    // El merge cambia deals y owners: refrescar duplicados (misma página/
    // búsqueda) y snapshot.
    thunkAPI.dispatch(loadDealDuplicates(currentDuplicatesParams(thunkAPI.getState())));
    thunkAPI.dispatch(loadSnapshot());
    return result;
});

export const loadAssignmentQueue = createAsyncThunk<
    QueuePage,
    PageParams,
    { state: RootState }
>('panel_comerciales/loadAssignmentQueue', async (params, thunkAPI) => {
    const token = thunkAPI.getState().User?.token ?? '';
    return getAssignmentQueue(token, params);
});

export const assignFromQueueThunk = createAsyncThunk<
    { usersId: number; sellerId: number; dealId: number | null },
    { payload: QueueAssignPayload; queuePage: PageParams },
    { state: RootState }
>('panel_comerciales/assignFromQueue', async ({ payload, queuePage }, thunkAPI) => {
    const token = thunkAPI.getState().User?.token ?? '';
    const result = await assignFromQueue(token, payload);
    // La fila salió de la cola y el seller consumió cupo: refrescar ambos.
    thunkAPI.dispatch(loadAssignmentQueue(queuePage));
    thunkAPI.dispatch(loadSnapshot());
    return result;
});

export const assignFromQueueBatchThunk = createAsyncThunk<
    BatchAssignResult,
    { payload: QueueAssignBatchPayload; queuePage: PageParams },
    { state: RootState }
>('panel_comerciales/assignFromQueueBatch', async ({ payload, queuePage }, thunkAPI) => {
    const token = thunkAPI.getState().User?.token ?? '';
    const result = await assignFromQueueBatch(token, payload);
    // Varias filas salieron de la cola y el comercial consumió cupo.
    thunkAPI.dispatch(loadAssignmentQueue(queuePage));
    thunkAPI.dispatch(loadSnapshot());
    return result;
});

// ---------------------------------------------------------------------------
// Secciones paginadas: notificaciones y actividad reciente (ago-2026)
// ---------------------------------------------------------------------------

export const loadNotifications = createAsyncThunk<
    NotificationsPage,
    PageParams,
    { state: RootState }
>('panel_comerciales/loadNotifications', async (params, thunkAPI) => {
    const token = thunkAPI.getState().User?.token ?? '';
    return getNotificationsPage(token, params);
});

export const loadActivity = createAsyncThunk<
    ActivityPage,
    PageParams,
    { state: RootState }
>('panel_comerciales/loadActivity', async (params, thunkAPI) => {
    const token = thunkAPI.getState().User?.token ?? '';
    return getActivityPage(token, params);
});
